// Utilitaire de tracking Google Analytics via GTM
// Système complet de suivi des événements pour Sun-Spot

// Vérifier si GTM est disponible
const isGTMAvailable = () => {
  return typeof window !== 'undefined' && window.dataLayer;
};

// Fonction générique pour envoyer des événements
const trackEvent = (action, category, label = '', value = 0, customParams = {}) => {
  if (!isGTMAvailable()) {
    console.log('GTM not available, event would be:', { action, category, label, value, customParams });
    return;
  }

  window.dataLayer.push({
    event: 'custom_event',
    event_category: category,
    event_action: action,
    event_label: label,
    value: value,
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
    page_path: window.location.pathname,
    ...customParams
  });

  console.log('📊 Analytics Event:', { action, category, label, value });
};

// === ÉVÉNEMENTS DE NAVIGATION ===
export const trackNavigation = (pageName, fromPage = '') => {
  trackEvent('page_view', 'navigation', pageName, 0, {
    from_page: fromPage,
    user_agent: navigator.userAgent,
    screen_resolution: `${screen.width}x${screen.height}`,
    viewport_size: `${window.innerWidth}x${window.innerHeight}`
  });
};

export const trackMenuClick = (menuItem, isMobile = false) => {
  trackEvent('menu_click', 'navigation', menuItem, 0, {
    device_type: isMobile ? 'mobile' : 'desktop',
    menu_type: isMobile ? 'hamburger' : 'desktop_nav'
  });
};

export const trackCarnetDeSpots = (location, action = 'click') => {
  trackEvent(action, 'carnet_de_spots', location, 0, {
    button_type: 'carnet_navigation',
    special_feature: true
  });
};

// === ÉVÉNEMENTS D'INTERACTION ===
export const trackCTAClick = (ctaText, location) => {
  trackEvent('cta_click', 'conversion', ctaText, 1, {
    cta_location: location,
    conversion_potential: 'high'
  });
};

export const trackContactClick = (contactType = 'email') => {
  trackEvent('contact_click', 'engagement', contactType, 1, {
    contact_method: contactType,
    conversion_intent: 'high'
  });
};

export const trackFooterClick = (linkName) => {
  trackEvent('footer_click', 'engagement', linkName, 0, {
    footer_section: 'links'
  });
};

// === ÉVÉNEMENTS DE CONTENU ===
export const trackScrollDepth = (percentage, pageName) => {
  trackEvent('scroll_depth', 'engagement', `${percentage}%`, percentage, {
    page_name: pageName,
    engagement_level: percentage > 75 ? 'high' : percentage > 50 ? 'medium' : 'low'
  });
};

export const trackTimeOnPage = (timeInSeconds, pageName) => {
  trackEvent('time_on_page', 'engagement', pageName, timeInSeconds, {
    engagement_duration: timeInSeconds > 60 ? 'long' : timeInSeconds > 30 ? 'medium' : 'short'
  });
};

export const trackSectionView = (sectionName, timeToView = 0) => {
  trackEvent('section_view', 'content', sectionName, timeToView, {
    section_engagement: 'viewed',
    time_to_view: timeToView
  });
};

// === ÉVÉNEMENTS BLOG/CARNET ===
export const trackBlogInteraction = (action, articleTitle = '', location = '') => {
  trackEvent(action, 'blog_carnet', articleTitle || location, 0, {
    blog_action: action,
    content_type: 'carnet_de_spots'
  });
};

export const trackSpotView = (spotName, viewDuration = 0) => {
  trackEvent('spot_view', 'content', spotName, viewDuration, {
    content_type: 'spot_article',
    engagement_time: viewDuration
  });
};

// === ÉVÉNEMENTS PERFORMANCE ===
export const trackPageLoad = (loadTime, pageName) => {
  trackEvent('page_load', 'performance', pageName, loadTime, {
    load_time_ms: loadTime,
    performance_category: loadTime < 2000 ? 'fast' : loadTime < 4000 ? 'medium' : 'slow'
  });
};

export const trackAnimationInteraction = (action, animationType = 'time_cycle') => {
  trackEvent(action, 'animation', animationType, 0, {
    animation_type: animationType,
    user_interaction: action
  });
};

// === ÉVÉNEMENTS D'ERREUR ===
export const trackError = (errorMessage, errorLocation) => {
  trackEvent('error', 'technical', errorLocation, 0, {
    error_message: errorMessage,
    error_type: 'frontend'
  });
};

// === ÉVÉNEMENTS DEVICE & BROWSER ===
export const trackDeviceInfo = () => {
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  const isTablet = /iPad|Tablet/i.test(navigator.userAgent);
  
  trackEvent('device_detection', 'technical', 'device_info', 0, {
    device_type: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
    browser: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    connection: navigator.connection ? navigator.connection.effectiveType : 'unknown'
  });
};

// === ÉVÉNEMENTS PERSONNALISÉS AVANCÉS ===
export const trackUserJourney = (step, journeyType = 'discovery') => {
  trackEvent('user_journey', 'funnel', step, 0, {
    journey_type: journeyType,
    funnel_step: step
  });
};

export const trackFeatureUsage = (featureName, usageType = 'click') => {
  trackEvent('feature_usage', 'product', featureName, 0, {
    feature_type: featureName,
    usage_pattern: usageType
  });
};

// === TRACKING AUTOMATIQUE ===
// Tracking automatique du scroll depth
let scrollTrackingThresholds = [25, 50, 75, 90, 100];
let trackedScrollDepths = new Set();

export const initScrollTracking = (pageName) => {
  if (typeof window === 'undefined') return;
  
  const trackScrollDepthThrottled = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercentage = Math.round((scrollTop / docHeight) * 100);

    scrollTrackingThresholds.forEach(threshold => {
      if (scrollPercentage >= threshold && !trackedScrollDepths.has(threshold)) {
        trackedScrollDepths.add(threshold);
        trackScrollDepth(threshold, pageName);
      }
    });
  };

  let scrollTimeout;
  const handleScroll = () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(trackScrollDepthThrottled, 150);
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  
  // Nettoyage
  return () => {
    window.removeEventListener('scroll', handleScroll);
    trackedScrollDepths.clear();
  };
};

// Tracking automatique du temps sur la page
export const initTimeTracking = (pageName) => {
  if (typeof window === 'undefined') return;
  
  const startTime = Date.now();
  let timeTrackingIntervals = [15, 30, 60, 120, 300]; // 15s, 30s, 1min, 2min, 5min
  let trackedTimeIntervals = new Set();

  const trackTimeInterval = () => {
    const timeSpent = Math.round((Date.now() - startTime) / 1000);
    
    timeTrackingIntervals.forEach(threshold => {
      if (timeSpent >= threshold && !trackedTimeIntervals.has(threshold)) {
        trackedTimeIntervals.add(threshold);
        trackTimeOnPage(timeSpent, pageName);
      }
    });
  };

  const timeInterval = setInterval(trackTimeInterval, 5000); // Check every 5s

  // Track final time on page unload
  const handleUnload = () => {
    const finalTime = Math.round((Date.now() - startTime) / 1000);
    trackTimeOnPage(finalTime, `${pageName}_final`);
  };

  window.addEventListener('beforeunload', handleUnload);

  // Nettoyage
  return () => {
    clearInterval(timeInterval);
    window.removeEventListener('beforeunload', handleUnload);
    trackedTimeIntervals.clear();
  };
};

// === TRACKING DE PERFORMANCE ===
export const trackPagePerformance = (pageName) => {
  if (typeof window === 'undefined' || !window.performance) return;

  // Mesurer le temps de chargement après que tout soit chargé
  window.addEventListener('load', () => {
    setTimeout(() => {
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation) {
        const loadTime = navigation.loadEventEnd - navigation.fetchStart;
        trackPageLoad(Math.round(loadTime), pageName);
        
        // Métriques détaillées
        trackEvent('performance_metrics', 'technical', pageName, loadTime, {
          dns_time: Math.round(navigation.domainLookupEnd - navigation.domainLookupStart),
          connect_time: Math.round(navigation.connectEnd - navigation.connectStart),
          response_time: Math.round(navigation.responseEnd - navigation.requestStart),
          dom_load_time: Math.round(navigation.domContentLoadedEventEnd - navigation.fetchStart),
          full_load_time: Math.round(loadTime)
        });
      }
    }, 1000);
  });
};

// === INITIALISATION COMPLÈTE ===
export const initAnalytics = (pageName) => {
  if (typeof window === 'undefined') return;

  // Tracking initial
  trackDeviceInfo();
  trackNavigation(pageName);
  trackPagePerformance(pageName);
  
  // Tracking continu
  const cleanupScroll = initScrollTracking(pageName);
  const cleanupTime = initTimeTracking(pageName);
  
  // Retourner la fonction de nettoyage
  return () => {
    if (cleanupScroll) cleanupScroll();
    if (cleanupTime) cleanupTime();
  };
};

// === TRACKING D'ERREURS AUTOMATIQUE ===
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    trackError(event.message, event.filename || 'unknown');
  });

  window.addEventListener('unhandledrejection', (event) => {
    trackError(event.reason?.message || 'Promise rejection', 'promise');
  });
}